import { Component, For } from 'solid-js';
import { OptionalTierLock } from '@trimble/tiering';
import { UnreachableCaseError } from 'ts-essentials';
import type { AnchoredContextMenuProps } from './AnchoredContextMenu.types';
import type { IconMenuItem } from './IconContextMenu.types';

export * from './AnchoredContextMenu.types';

/** 16px vertical group padding and no border, matching other icon overflow menus. */
const ICON_CONTEXT_MENU_STYLES = `
  .icon-context-menu modus-wc-menu ul.modus-wc-menu {
    padding-top: 1rem;
    padding-bottom: 1rem;
    border: none;
    border-radius: 0;
  }
`;

type DropdownMenuElement = HTMLElement & {
  menuVisible: boolean;
};

function resolveMenuPlacement(
  menuPlacement: AnchoredContextMenuProps['menuPlacement'],
  tabAligned: boolean | undefined
): string {
  const placement = menuPlacement ?? 'bottom';
  switch (placement) {
    case 'right':
      return 'right-start';
    case 'left':
      return 'left-start';
    case 'bottom':
      return tabAligned ? 'bottom-end' : 'bottom-start';
    default:
      throw new UnreachableCaseError(placement);
  }
}

function stopPropagation(event: Event): void {
  event.stopPropagation();
}

export const AnchoredContextMenu: Component<AnchoredContextMenuProps> = (
  props
) => {
  let dropdownRef: DropdownMenuElement | undefined;

  const handleMenuVisibilityChange = (
    event: CustomEvent<{ isVisible: boolean }>
  ): void => {
    props.onOpenChange?.(event.detail.isVisible);
  };

  const handleItemSelect = (
    event: CustomEvent<{ value: string }>,
    item: IconMenuItem
  ): void => {
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    stopPropagation(event);
    // Close before onSelection — selection often re-renders the host (e.g. isolate
    // toggles menu items) and closing afterward can leave a fixed menu painted over
    // the viewer.
    if (dropdownRef) {
      dropdownRef.menuVisible = false;
    }
    props.onSelection?.({
      event,
      value: props.value,
      menuItem: event.detail.value,
    });
  };

  return (
    <div
      class="inline-flex shrink-0"
      data-testid={props.name ? `iconcontextmenu-${props.name}` : null}
      onClick={stopPropagation}
    >
      <style textContent={ICON_CONTEXT_MENU_STYLES} />
      <modus-wc-dropdown-menu
        ref={(el: DropdownMenuElement) => {
          dropdownRef = el;
        }}
        button-variant="borderless"
        button-color="tertiary"
        button-shape="square"
        button-size="sm"
        button-aria-label={props.name || 'Actions menu'}
        menu-placement={resolveMenuPlacement(
          props.menuPlacement,
          props.tabAligned
        )}
        prop:menuBordered={false}
        menu-size="md"
        menu-strategy="fixed"
        menu-offset={4}
        custom-class="icon-context-menu bg-transparent"
        attr:data-testid={
          props.name ? `iconcontextmenu-dropdown-${props.name}` : undefined
        }
        on:menuVisibilityChange={handleMenuVisibilityChange}
        onClick={stopPropagation}
      >
        <div slot="button" class="flex items-center justify-center">
          {props.children}
        </div>
        <div slot="menu">
          <For each={props.menuItems}>
            {(item) => (
              <OptionalTierLock options={item.accessConfig}>
                <modus-wc-menu-item
                  prop:label={item.label}
                  prop:value={item.value}
                  size="md"
                  prop:bordered={false}
                  prop:disabled={item.disabled ?? false}
                  prop:tooltipContent={
                    item.disabled ? item.disabledReasonTooltip : undefined
                  }
                  attr:data-testid={`context-menu-item-${item.value}`}
                  on:itemSelect={(event: CustomEvent<{ value: string }>) =>
                    handleItemSelect(event, item)
                  }
                />
              </OptionalTierLock>
            )}
          </For>
        </div>
      </modus-wc-dropdown-menu>
    </div>
  );
};

export default AnchoredContextMenu;
